import useAuthenticatedApi, {
    HTTP_STATUS_CODES,
} from '../../../Authentication/components/hooks/useAuthenticatedApi';

export interface GallerySlideshow {
    id: number;
    galleryId: number;
    name: string;
    tags: string[];
    matchMode: 'any' | 'all';
}

export interface ActivationTag {
    tag: string;
    count: number;
}

export default function useSlideshows() {
    const authenticatedApi = useAuthenticatedApi();

    const getSlideshows = async (
        galleryId: number,
    ): Promise<GallerySlideshow[]> => {
        const response = await authenticatedApi.get(
            `/galleries/slideshows/${galleryId}`,
        );

        const { data } = response;
        return data;
    };

    const createSlideshow = async (
        galleryId: number,
        slideshow: Pick<GallerySlideshow, 'name' | 'tags' | 'matchMode'>,
    ): Promise<GallerySlideshow | null> => {
        const response = await authenticatedApi.post(
            `/galleries/slideshows/${galleryId}`,
            JSON.stringify(slideshow),
        );

        return response.status === HTTP_STATUS_CODES.OK
            ? response.data
            : null;
    };

    const updateSlideshow = async (
        slideshowId: number,
        slideshow: Pick<GallerySlideshow, 'name' | 'tags' | 'matchMode'>,
    ): Promise<GallerySlideshow | null> => {
        const response = await authenticatedApi.put(
            `/galleries/slideshows/edit/${slideshowId}`,
            JSON.stringify(slideshow),
        );

        return response.status === HTTP_STATUS_CODES.OK
            ? response.data
            : null;
    };

    const deleteSlideshow = async (slideshowId: number): Promise<boolean> => {
        const response = await authenticatedApi.delete(
            `/galleries/slideshows/edit/${slideshowId}`,
        );

        return response.status === HTTP_STATUS_CODES.OK;
    };

    // Distinct tags already applied to the activation's media, for the tag
    // picker. Free-form entry stays available so a slideshow can be set up
    // before the event has captured anything.
    const getActivationTags = async (
        activationId: number,
    ): Promise<ActivationTag[]> => {
        const response = await authenticatedApi.get(
            `/media-tags/activation/${activationId}`,
        );

        const { data } = response;
        return data;
    };

    return {
        getSlideshows,
        createSlideshow,
        updateSlideshow,
        deleteSlideshow,
        getActivationTags,
    };
}
